import { arrayMove } from '@dnd-kit/sortable';
import type { AnalyticsStatMetadata } from '~/hooks/queries/useAnalyticsStatMetadataQuery';
import type { ColumnRef } from '~/hooks/queries/useAnalyticsStatsViewsQuery';

export type TaggedItem = { id: string; label: string; tooltip?: string; tags?: string[] };

/**
 * Returns the full column layout for "all columns" mode: all allowed groupings, stats, and displays.
 */
export function getAllowedColumnLayout(metadata: AnalyticsStatMetadata): ColumnRef[] {
  const { allowed } = metadata;
  const grouping: ColumnRef[] = allowed.groupBy.map((groupKey) => ({
    kind: 'grouping',
    key: groupKey,
  }));
  const stat: ColumnRef[] = allowed.statColumns.map((columnAlias) => ({
    kind: 'stat',
    alias: columnAlias,
  }));
  const display: ColumnRef[] = allowed.displayColumns.map((columnAlias) => ({
    kind: 'display',
    alias: columnAlias,
  }));
  return [...grouping, ...stat, ...display];
}

/**
 * Filters columnLayout to only include refs that are present in the stat's allowed surface.
 * Used when loading a saved view whose layout may reference columns no longer in allowed.
 */
/** Resolves a saved layout: null/undefined means all allowed columns; otherwise clips to allowed. */
export function resolveSavedColumnLayout(
  layout: ColumnRef[] | null | undefined,
  metadata: AnalyticsStatMetadata | undefined,
): ColumnRef[] {
  if (!metadata) {
    return layout ?? [];
  }
  if (layout === null || layout === undefined) {
    return getAllowedColumnLayout(metadata);
  }
  return clipColumnLayoutToAllowed(layout, metadata);
}

/**
 * Columns to label for a saved view: the view's selection is authoritative.
 * Null/undefined layout means all columns allowed by metadata (e.g. "all columns" reports).
 */
export function getViewColumnLayoutForLabels(
  columnLayout: ColumnRef[] | null | undefined,
  metadata: AnalyticsStatMetadata,
): ColumnRef[] {
  if (columnLayout === null || columnLayout === undefined) {
    return getAllowedColumnLayout(metadata);
  }
  return columnLayout;
}

export function clipColumnLayoutToAllowed(
  layout: ColumnRef[] | null,
  metadata: AnalyticsStatMetadata,
): ColumnRef[] {
  if (!layout || layout.length === 0) return [];

  const { allowed } = metadata;
  const allowedGroupBy = new Set(allowed.groupBy);
  const allowedStatColumns = new Set(allowed.statColumns);
  const allowedDisplayColumns = new Set(allowed.displayColumns);

  return layout.filter((columnRef) => {
    if (columnRef.kind === 'grouping') return allowedGroupBy.has(columnRef.key);
    if (columnRef.kind === 'stat') return allowedStatColumns.has(columnRef.alias);
    if (columnRef.kind === 'display') return allowedDisplayColumns.has(columnRef.alias);
    return false;
  });
}

/**
 * Returns true if the ref is allowed by metadata.
 */
export function isRefInAllowed(ref: ColumnRef, metadata: AnalyticsStatMetadata): boolean {
  if (ref.kind === 'grouping') return metadata.allowed.groupBy.includes(ref.key);
  if (ref.kind === 'stat') return metadata.allowed.statColumns.includes(ref.alias);
  if (ref.kind === 'display') return metadata.allowed.displayColumns.includes(ref.alias);
  return false;
}

/**
 * Returns a stable string ID for a ColumnRef for use as a React key or dnd-kit id.
 */
export function columnRefToId(ref: ColumnRef): string {
  if (ref.kind === 'grouping') return `grouping:${ref.key}`;
  if (ref.kind === 'stat') return `stat:${ref.alias}`;
  return `display:${ref.alias}`;
}

/** Accessor key for row data: key for grouping, alias for stat/display. */
export function getColumnRefAccessor(ref: ColumnRef): string {
  if (ref.kind === 'grouping') return ref.key;
  return ref.alias;
}

export function getColumnRefLabel(
  ref: ColumnRef,
  metadata: AnalyticsStatMetadata,
): string | undefined {
  if (ref.kind === 'grouping') {
    return metadata.groupingsByKey[ref.key]?.label;
  }
  if (ref.kind === 'stat') {
    return metadata.statColumnsByAlias[ref.alias]?.label;
  }
  return metadata.displayColumnsByAlias[ref.alias]?.label;
}

export function getColumnRefTooltip(
  ref: ColumnRef,
  metadata: AnalyticsStatMetadata,
): string | undefined {
  if (ref.kind === 'grouping') {
    return metadata.groupingsByKey[ref.key]?.tooltip ?? undefined;
  }
  if (ref.kind === 'stat') {
    return metadata.statColumnsByAlias[ref.alias]?.tooltip ?? undefined;
  }
  return metadata.displayColumnsByAlias[ref.alias]?.tooltip ?? undefined;
}

/**
 * Returns display column aliases that belong to the given dimension (idColumnAlias matches groupKey).
 */
export function getDisplayColumnsForDimension(
  metadata: AnalyticsStatMetadata,
  groupKey: string,
): string[] {
  return metadata.allowed.displayColumns.filter((columnAlias) => {
    const column = metadata.displayColumnsByAlias[columnAlias];
    return column?.idColumnAlias === groupKey;
  });
}

export function taggedItemToColumnRef(item: TaggedItem, kind: 'stat' | 'display'): ColumnRef {
  return kind === 'stat' ? { kind: 'stat', alias: item.id } : { kind: 'display', alias: item.id };
}

/** Adds each ref not already in layout; uses metadata for display column placement rules. */
export function addMissingRefsToLayout(
  layout: ColumnRef[],
  refs: ColumnRef[],
  metadata?: AnalyticsStatMetadata,
): ColumnRef[] {
  let nextLayout = layout;
  for (const ref of refs) {
    if (!hasRefInLayout(nextLayout, ref)) {
      nextLayout = toggleRefInLayout(nextLayout, ref, metadata);
    }
  }
  return nextLayout;
}

/** True if layout already includes this ref (by key for grouping, alias for stat/display). */
export function hasRefInLayout(layout: ColumnRef[], ref: ColumnRef): boolean {
  if (ref.kind === 'grouping') {
    return layout.some((layoutRef) => layoutRef.kind === 'grouping' && layoutRef.key === ref.key);
  }
  if (ref.kind === 'stat') {
    return layout.some((layoutRef) => layoutRef.kind === 'stat' && layoutRef.alias === ref.alias);
  }
  return layout.some((layoutRef) => layoutRef.kind === 'display' && layoutRef.alias === ref.alias);
}

/**
 * Adds or removes ref in layout. Idempotent for add (no duplicates).
 *
 * Remove: filters out the matching ref. For groupings, when metadata is provided, also removes
 * display refs whose idColumnAlias matches the grouping key.
 * Add: grouping refs are inserted before the first stat/display ref. Stat refs are appended.
 * Display refs: when metadata is provided, inserts after its idColumnAlias dimension (or after
 * the last display for that dimension); otherwise appends.
 */
export function toggleRefInLayout(
  layout: ColumnRef[],
  ref: ColumnRef,
  metadata?: AnalyticsStatMetadata,
): ColumnRef[] {
  if (hasRefInLayout(layout, ref)) {
    if (ref.kind === 'grouping') {
      const displayAliasesToRemove = metadata
        ? new Set(getDisplayColumnsForDimension(metadata, ref.key))
        : new Set<string>();
      return layout.filter((layoutRef) => {
        if (layoutRef.kind === 'grouping' && layoutRef.key === ref.key) return false;
        if (
          layoutRef.kind === 'display' &&
          layoutRef.alias &&
          displayAliasesToRemove.has(layoutRef.alias)
        )
          return false;
        return true;
      });
    }
    if (ref.kind === 'stat') {
      return layout.filter(
        (layoutRef) => !(layoutRef.kind === 'stat' && layoutRef.alias === ref.alias),
      );
    }
    return layout.filter(
      (layoutRef) => !(layoutRef.kind === 'display' && layoutRef.alias === ref.alias),
    );
  }
  if (ref.kind === 'grouping') {
    const firstStatIndex = layout.findIndex((layoutRef) => layoutRef.kind === 'stat');
    if (firstStatIndex === -1) return [...layout, ref];
    return [...layout.slice(0, firstStatIndex), ref, ...layout.slice(firstStatIndex)];
  }
  if (ref.kind === 'display' && metadata) {
    const idColumnAlias = metadata.displayColumnsByAlias[ref.alias]?.idColumnAlias;
    if (idColumnAlias) {
      const dimensionRef: ColumnRef = { kind: 'grouping', key: idColumnAlias };
      let workingLayout = layout;
      if (!hasRefInLayout(layout, dimensionRef)) {
        workingLayout = toggleRefInLayout(layout, dimensionRef, metadata);
      }
      const dimensionIndex = workingLayout.findIndex(
        (layoutRef) => layoutRef.kind === 'grouping' && layoutRef.key === idColumnAlias,
      );
      if (dimensionIndex !== -1) {
        let insertAfter = dimensionIndex;
        for (let index = dimensionIndex + 1; index < workingLayout.length; index += 1) {
          const layoutRef = workingLayout[index];
          if (
            layoutRef.kind === 'display' &&
            metadata.displayColumnsByAlias[layoutRef.alias]?.idColumnAlias === idColumnAlias
          ) {
            insertAfter = index;
          } else {
            break;
          }
        }
        return [
          ...workingLayout.slice(0, insertAfter + 1),
          ref,
          ...workingLayout.slice(insertAfter + 1),
        ];
      }
    }
  }
  return [...layout, ref];
}

/** Stat metadata column `tooltip` from API (may be null). */
export type ColumnMetadataTooltip = string | null | undefined;

export function columnTooltipForPicker(tooltip: ColumnMetadataTooltip): string | undefined {
  return tooltip ?? undefined;
}

/**
 * Reorders columnLayout by moving item from oldIndex to newIndex.
 * Uses same logic as dnd-kit arrayMove for consistency.
 */
export function reorderColumnLayout(
  layout: ColumnRef[],
  oldIndex: number,
  newIndex: number,
): ColumnRef[] {
  return arrayMove(layout, oldIndex, newIndex);
}

/**
 * Groups items by tag. Items with no tags are excluded (ordering handled by API sort).
 * Multi-tagged items appear in each tag's section (same item.id, shared selection).
 */
export function groupByTagHeader(items: TaggedItem[]): Map<string, TaggedItem[]> {
  const map = new Map<string, TaggedItem[]>();
  for (const item of items) {
    if (item.tags?.length) {
      for (const tagName of [...new Set(item.tags)]) {
        const existing = map.get(tagName) ?? [];
        existing.push(item);
        map.set(tagName, existing);
      }
    }
  }
  return map;
}
